import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { showError, showSuccess } from "../../helpers/toast";
import axios from "axios";
import { Endpoints } from "../../constants/apiEndPoints";
import qs from "qs";
import _ from "lodash";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

API.interceptors.response.use(
  (response) => {
    // showSuccess(response.data.message);
    return Promise.resolve(response);
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.clear();
      window.location.href = "/";
    }
    showError(
      error.response?.data?.error ??
      error.response?.data?.message ??
      error.message
    );
    return Promise.reject(error);
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${userData.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.put(`/users/${userId}`, userData, config);
      showSuccess("Update Successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export const getUser = createAsyncThunk(
  "users/getUser",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${userData.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.get(`/users/${userId}`, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

// Async thunk for deleting a company
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async ({ configdata }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${configdata.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.delete(`/users/${configdata.user_id}`, config);
      return configdata.user_id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

export const getallUser = createAsyncThunk(
  "users/getallUser",
  async ({ authToken, queryParams }, { rejectWithValue }) => {
    console.log(authToken, queryParams);
    try {
      let url = "/users/";
      if (queryParams && !_.isEmpty(queryParams)) {
        url = `${url}?${queryParams instanceof Object
          ? qs.stringify(queryParams)
          : queryParams
          }`;
      }
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${authToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.get(url, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

export const updatePassword = createAsyncThunk(
  "users/updatePassword",
  async ({ userId, userData, token, onSuccess }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.put(
        `/users/change-password/${userId}`,
        userData,
        config
      );
      onSuccess();
      // showSuccess("Update Successfully!");
      return response.data;
    } catch (error) {
      console.log(error, "rejected");
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export const sendMail = createAsyncThunk(
  "users/sendMail",
  async ({ mailData, accessToken }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.post("/users/sendMail", mailData, config);
      showSuccess("Mail sent successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send mail"
      );
    }
  }
);

export const reactivateUser = createAsyncThunk(
  "users/reactivateUser",
  async ({ email, company_id, accessToken }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.post(
        Endpoints.reActivation,
        { email, company_id },
        config
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || "Failed to reactivate user"
      );
    }
  }
);

export const resendEmail = createAsyncThunk(
  "users/resendEmail",
  async ({ authToken, email, company_id }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${authToken}`,
          "Content-Type": "application/json",
        },
      };
      // Use Endpoints.reActivation
      const response = await API.post(Endpoints.reActivation, { email, company_id }, config);
      showSuccess("Email sent successfully");
      return response.data;
    } catch (error) {
      showError(error.response?.data?.message || "Failed to resend email");
      return rejectWithValue(
        error.response?.data?.message || "Failed to resend email"
      );
    }
  }
);

const initialState = {
  getuserdata: {},
  getalluserdata: [],
  loading: false,
  error: null,
  message: null,
  updateLoader: false,
  passwordLoader: false,
  passwordError: null,
};

const userSlice = createSlice({
  name: "userdata",
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message;
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.getuserdata = action.payload;
        state.error = null;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getallUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getallUser.fulfilled, (state, action) => {
        state.loading = false;
        state.getalluserdata = action.payload;
        state.error = null;
      })
      .addCase(getallUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        // state.getalluserdata = state.getalluserdata.filter(
        //   (user) => user.id !== action.payload
        // );
        console.log(action, state.getalluserdata, "delete user");
        state.getalluserdata = state.getalluserdata.map((user) => {
          if (user.id == action.payload) {
            showSuccess(
              user.is_deleted ? "Restored Successfully" : "Delete Successfully"
            );
            return { ...user, is_deleted: !user.is_deleted };
          }
          return user;
        });
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePassword.pending, (state) => {
        state.passwordLoader = true;
        state.passwordError = null;
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.passwordLoader = false;
        state.message = action.payload?.message;
        state.passwordError = null;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.passwordLoader = false;
        state.passwordError = action.payload;
      })
      // Send Mail Cases
      .addCase(sendMail.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendMail.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message;
        state.error = null;
      })
      .addCase(sendMail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reactivate User Cases
      .addCase(reactivateUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(reactivateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message || "User reactivated successfully";
        state.error = null;
        showSuccess(state.message);
      })
      .addCase(reactivateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message;
        state.message = action.error?.message;
        showError(state.error);
      })
      // Resend Email Cases
      .addCase(resendEmail.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message || "Email sent successfully";
        state.error = null;
      })
      .addCase(resendEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});




export default userSlice.reducer;
